import type { ListTenantsSort, SortDirection } from '@t4s/api-client/model'

/**
 * The Tenants list's state, as it lives in the address bar — docs/ux/PLATFORM-ADMIN.md §7.2 (TAS-16).
 *
 * **The address is the state.** A reload, Back, a bookmark and a link sent to the other platform
 * account all land on the same list, because the screen reads nothing about the list from anywhere
 * but here (§5.5 rule 9: the screen is always the page for the URL).
 *
 * | Parameter | Values | Omitted when |
 * |---|---|---|
 * | `q` | a non-blank string | there is no search |
 * | `state` | `live`, `retired`, `any` | `live` |
 * | `sort` | `name`, `slug`, `createdAt` | `name` |
 * | `dir` | `asc`, `desc` | `asc` |
 * | `page` | an integer from 1 | `1` |
 * | `rescue` | `true` | the owner did not just arrive from a half-successful create (§8.4) |
 *
 * Defaults are left out so that the plain list has the plain address, `/tenants`, and so that two
 * addresses for one list cannot both be in circulation.
 *
 * This file is pure — no React, no router — so every rule below is tested without a screen.
 */

export type TenantStateFilter = 'live' | 'retired' | 'any'

/** What the route's `validateSearch` yields: every key optional, every default absent. */
export type TenantListSearch = {
  readonly q?: string | undefined
  readonly state?: TenantStateFilter | undefined
  readonly sort?: ListTenantsSort | undefined
  readonly dir?: SortDirection | undefined
  readonly page?: number | undefined
  readonly rescue?: true | undefined
}

/** The same, with every default written out — what the screen actually works with. */
export type ResolvedTenantList = {
  readonly q: string
  readonly state: TenantStateFilter
  readonly sort: ListTenantsSort
  readonly dir: SortDirection
  readonly page: number
}

export const TENANT_LIST_DEFAULTS: ResolvedTenantList = {
  q: '',
  state: 'live',
  sort: 'name',
  dir: 'asc',
  page: 1,
}

const STATES: readonly TenantStateFilter[] = ['live', 'retired', 'any']
const SORTS: readonly ListTenantsSort[] = ['name', 'slug', 'createdAt']
const DIRECTIONS: readonly SortDirection[] = ['asc', 'desc']

function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
  return allowed.find((candidate) => candidate === value)
}

/**
 * A page number from whatever the address held.
 *
 * The router parses a query value as JSON where it can, so `?page=2` arrives as the number `2`;
 * a hand-typed `?page=02` or a value from another tool arrives as a string. Both are accepted.
 * Anything that is not a whole number from 1 up — `0`, `-3`, `1.5`, `abc`, a number too large to
 * count with — is not a page, and the list shows page one rather than an error.
 *
 * **One form is a page whether or not the owner meant it to be: `?page=1e3`.** The router's JSON
 * parse turns it into the number `1000` before this function sees it, and `1000` is a whole number.
 * It is harmless — no estate has that page, so the list replaces it with the last one that exists —
 * and cannot be told apart from a typed `1000`. (The *string* `'1e3'`, which only a caller that
 * bypasses the router can produce, is refused.)
 */
function pageFrom(value: unknown): number | undefined {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^[0-9]+$/.test(value)
        ? Number(value)
        : Number.NaN
  return Number.isSafeInteger(parsed) && parsed > 1 ? parsed : undefined
}

/**
 * A search term from whatever the address held.
 *
 * The router's JSON parse turns `?q=2026` into the number `2026` and `?q=true` into a boolean, and
 * the owner meant the text they typed. A non-blank string is kept, trimmed; anything else — `null`,
 * an array, an object, whitespace — is no search.
 */
function searchFrom(value: unknown): string | undefined {
  const text =
    typeof value === 'string'
      ? value
      : typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : ''
  const trimmed = text.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * The route's `validateSearch`: a value from a URL is never trusted to be the type a screen expects
 * (the same rule `next` on `/login` follows). An invalid value is dropped and its default applies;
 * the address is not rewritten.
 */
export function parseTenantListSearch(raw: Record<string, unknown>): TenantListSearch {
  const q = searchFrom(raw.q)
  const state = oneOf(STATES, raw.state)
  const sort = oneOf(SORTS, raw.sort)
  const dir = oneOf(DIRECTIONS, raw.dir)
  const page = pageFrom(raw.page)

  // **Every key is always present in the result, even when it is `undefined`.** A route's search
  // is *merged* over its parent's, and the root route parses the raw query: leaving a refused key
  // out would let the raw value show through from above — `?state=bogus` would reach the screen as
  // `'bogus'` — which is precisely the value being refused. (`/login`'s `next` is written the same
  // way for the same reason.)
  return {
    q,
    // `live`, `name`, `asc` and page 1 are the defaults: written in an address they are accepted
    // and then dropped, so the screen has exactly one spelling of the plain list.
    state: state === TENANT_LIST_DEFAULTS.state ? undefined : state,
    sort: sort === TENANT_LIST_DEFAULTS.sort ? undefined : sort,
    dir: dir === TENANT_LIST_DEFAULTS.dir ? undefined : dir,
    page,
    rescue: raw.rescue === true || raw.rescue === 'true' ? true : undefined,
  }
}

/** A search with its defaults filled in. */
export function resolveTenantListSearch(search: TenantListSearch): ResolvedTenantList {
  return {
    q: search.q ?? TENANT_LIST_DEFAULTS.q,
    state: search.state ?? TENANT_LIST_DEFAULTS.state,
    sort: search.sort ?? TENANT_LIST_DEFAULTS.sort,
    dir: search.dir ?? TENANT_LIST_DEFAULTS.dir,
    page: search.page ?? TENANT_LIST_DEFAULTS.page,
  }
}

/**
 * The address for a list: the inverse of {@link resolveTenantListSearch}, with every default left
 * out. `rescue` is never carried — it explains one arrival (§8.4), and a list the owner has since
 * changed is no longer that arrival.
 */
export function tenantListSearchFrom(list: ResolvedTenantList): TenantListSearch {
  const q = list.q.trim()
  return {
    ...(q === '' ? {} : { q }),
    ...(list.state === TENANT_LIST_DEFAULTS.state ? {} : { state: list.state }),
    ...(list.sort === TENANT_LIST_DEFAULTS.sort ? {} : { sort: list.sort }),
    ...(list.dir === TENANT_LIST_DEFAULTS.dir ? {} : { dir: list.dir }),
    ...(list.page <= 1 ? {} : { page: list.page }),
  }
}

/**
 * What the header's count is a count of (§7.3). Said every time, because the number follows the
 * filter and used to say nothing of it: "30 tenants" under Live, "1 tenant" under a search, while
 * the estate held 40 (TAS-14, L1).
 *
 * - no search, live: `30 live tenants`
 * - no search, retired: `10 retired tenants`
 * - no search, both: `40 tenants`
 * - a search: `3 of 40 tenants match` — where 40 is the whole estate, both states. While the
 *   estate's size is not known (still loading, or its read failed) the search says what it can:
 *   `3 tenants match`.
 */
export function tenantCountLine(input: {
  readonly total: number
  readonly searching: boolean
  readonly state: TenantStateFilter
  readonly estateTotal: number | undefined
}): string {
  const { total, searching, state, estateTotal } = input
  const noun = (count: number) => (count === 1 ? 'tenant' : 'tenants')

  if (searching) {
    if (estateTotal === undefined) {
      return `${total} ${noun(total)} ${total === 1 ? 'matches' : 'match'}`
    }
    // The verb agrees with the estate: "1 of 40 tenants match", "0 of 1 tenant matches".
    return `${total} of ${estateTotal} ${noun(estateTotal)} ${estateTotal === 1 ? 'matches' : 'match'}`
  }

  const word = state === 'live' ? 'live ' : state === 'retired' ? 'retired ' : ''
  return `${total} ${word}${noun(total)}`
}
